import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { SupportedLanguages, UserRole, apiPostUsers } from "../../../api";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { PasswordInput } from "../../../components/ui-kit/fields/password-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { connectedRoles, managingRoles } from "../../../constants/constants";
import { supportedLanguages } from "../../../constants/enum-to-text";
import { assert, maybeAsArray } from "../../../utils/utils";
import { AdminConsole } from "../admin-console";
import { PatientsModal } from "../common/patients-modal";

const allRoles: UserRole[] = [
  UserRole["patient-ppg-app"],
  UserRole["physician-ppg-app"],
  UserRole["study-ppg-app"],
  UserRole.patient,
  UserRole.physician,
  UserRole["study-physician"],
  UserRole["seerlinq-user"],
];

export const AddUserDialog = observer(function AddUserDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminConsole;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const [fields] = useState(() =>
    observable({
      username: "",
      email: null as string | null,
      password: "",
      role: null as UserRole | null,
      preferred_language: SupportedLanguages.sk,
      connected_patient_id: null as number | null,
      visible_patient_ids: [] as number[],
    }),
  );

  async function save() {
    const { role } = fields;
    assert(role != null, "Role is required");

    await apiPostUsers({ body: { ...fields, role } });
    admin.api.clearResponseCache();
    onSaved();
    onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add new user</Trans>
      </h3>
      <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
        <div className="overflow-y-auto min-h-0 px-1 py-1">
          <FormTable dataAdd>
            <FormRow
              label={t`Username (case INsensitive)`}
              data={<TextInput attr="username" model={fields} required />}
              mandatory
            />
            <FormRow
              label={t`Email`}
              data={<TextInput attr="email" model={fields} required />}
              mandatory
            />
            <FormRow
              label={t`Password`}
              data={<PasswordInput attr="password" model={fields} required />}
              mandatory
            />
            <FormRow
              label={t`Role`}
              data={
                <Select
                  attr="role"
                  values={allRoles}
                  model={fields}
                  listTitle={t`Select role`}
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Preferred language`}
              data={
                <Select
                  attr="preferred_language"
                  values={supportedLanguages}
                  model={fields}
                  required
                />
              }
              mandatory
            />

            {fields.role != null && connectedRoles.includes(fields.role) && (
              <FormRow
                label={t`Connected patient`}
                data={
                  <ChooseField
                    label={
                      fields.connected_patient_id != null
                        ? String(fields.connected_patient_id)
                        : t`None`
                    }
                    modal={({ onClose }) => {
                      const patients = admin.createPatients();
                      return (
                        <PatientsModal
                          title={t`Choose connected ID for a new user`}
                          preselected={
                            maybeAsArray(fields.connected_patient_id) ??
                            undefined
                          }
                          mode="single"
                          model={patients}
                          onClose={onClose}
                          onConfirm={({ selected }) => {
                            fields.connected_patient_id = selected[0];
                          }}
                        />
                      );
                    }}
                  />
                }
              />
            )}

            {fields.role != null && managingRoles.includes(fields.role) && (
              <FormRow
                label={t`Managing patients`}
                data={
                  <ChooseField
                    count={fields.visible_patient_ids.length}
                    modal={({ onClose }) => {
                      const patients = admin.createPatients();
                      return (
                        <PatientsModal
                          title={t`Choose managed patient for a new managing user`}
                          preselected={fields.visible_patient_ids}
                          mode="multi"
                          model={patients}
                          onClose={onClose}
                          onConfirm={({ selected }) => {
                            fields.visible_patient_ids = selected;
                          }}
                        />
                      );
                    }}
                  />
                }
              />
            )}
          </FormTable>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit">
            <Trans>Add user</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});
